import React, { useState } from "react";
import { AddIcon, DeleteIcon, DragHandleIcon, ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  VStack,
  HStack,
  Input,
  Select,
  IconButton,
  FormControl,
  FormLabel,
  Heading,
  Divider,
  useToast,
  useColorModeValue as mode,
} from "@chakra-ui/react";
import { insertChecklist, editChecklist } from "../../redux/actions/checklistActions";
import { useDispatch } from "react-redux";
import { gameConsoleList } from "../../global-client";

const ITEM_TYPES = {
  CHECKBOX: "checkbox",
  MULTIPLE_CHOICE: "multipleChoice",
  THREE_OPTION: "threeOption",
  TEXT: "text",
  NUMERIC: "numeric",
  IMAGE: "image",
};

const gameConsoles = gameConsoleList();

const SetupCheckList = ({ editingChecklist, onSaveComplete }) => {
  const [checklistName, setChecklistName] = useState(editingChecklist?.checklistName || "");
  const [selectedConsoleId, setSelectedConsoleId] = useState(editingChecklist?.consoleId || "");
  const [selectedModelId, setSelectedModelId] = useState(editingChecklist?.modelId || "ALL");
  const [selectedProgress, setSelectedProgress] = useState(editingChecklist?.progress || "On Take-in");
  const [items, setItems] = useState(editingChecklist?.items.map((item, idx) => ({ ...item, id: idx })) || []);
  const [editingId, setEditingId] = useState(editingChecklist?._id || null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const toast = useToast();
  const dispatch = useDispatch();

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (index) => {
    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      return;
    }

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);
    setItems(newItems);
    setDraggedIndex(null);
  };

  const getSelectedConsole = () => {
    return gameConsoles.find((c) => c.id === selectedConsoleId);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        id: Date.now(),
        type: ITEM_TYPES.CHECKBOX,
        question: "",
        options: [""],
      },
    ]);
  };

  const removeItem = (id) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const updateItem = (id, field, value) => {
    setItems(items.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const addOption = (itemId) => {
    setItems(items.map((item) => (item.id === itemId ? { ...item, options: [...item.options, ""] } : item)));
  };

  const updateOption = (itemId, optionIndex, value) => {
    setItems(
      items.map((item) =>
        item.id === itemId
          ? {
              ...item,
              options: item.options.map((opt, idx) => (idx === optionIndex ? value : opt)),
            }
          : item
      )
    );
  };

  const removeOption = (itemId, optionIndex) => {
    setItems(
      items.map((item) =>
        item.id === itemId
          ? {
              ...item,
              options: item.options.filter((_, idx) => idx !== optionIndex),
            }
          : item
      )
    );
  };

  const moveItemUp = (index) => {
    if (index > 0) {
      const newItems = [...items];
      [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
      setItems(newItems);
    }
  };

  const moveItemDown = (index) => {
    if (index < items.length - 1) {
      const newItems = [...items];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      setItems(newItems);
    }
  };

  const handleClearForm = () => {
    setEditingId(null);
    setChecklistName("");
    setSelectedConsoleId("");
    setSelectedModelId("ALL");
    setSelectedProgress("On Take-in");
    setItems([]);
  };

  const handleSave = async () => {
    if (!checklistName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a checklist name",
        status: "error",
        duration: 3000,
      });
      return;
    }

    const checklistData = {
      checklistName: checklistName,
      consoleId: selectedConsoleId || null,
      //modelId: selectedModelId === "ALL" ? null : selectedModelId,
      modelId: selectedModelId,
      progress: selectedProgress,
      items: items.map(({ id, ...item }) => item),
    };

    try {
      if (editingId) {
        await dispatch(editChecklist(editingId, checklistData));
        toast({
          title: "Success",
          description: "Checklist updated successfully",
          status: "success",
          duration: 3000,
        });
      } else {
        await dispatch(insertChecklist(checklistData));
        toast({
          title: "Success",
          description: "Checklist saved successfully",
          status: "success",
          duration: 3000,
        });
      }
      handleClearForm();
      if (onSaveComplete) {
        onSaveComplete();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save checklist",
        status: "error",
        duration: 3000,
      });
    }
  };

  return (
    <Box
      borderRadius='lg'
      shadow='sm'
      border='1px'
      borderColor={mode("gray.200", "gray.700")}
      p={6}
      bg={mode("white", "gray.800")}
    >
      <Heading size='md' mb={6} fontWeight='600'>
        {editingId ? "Edit Checklist" : "Create New Checklist"}
      </Heading>

      <VStack spacing={4} mb={6} align='stretch'>
        <FormControl>
          <FormLabel fontWeight='600' fontSize='sm' mb={1}>
            Checklist Name
          </FormLabel>
          <Input
            value={checklistName}
            onChange={(e) => setChecklistName(e.target.value)}
            placeholder='Enter checklist name'
            size='sm'
            borderRadius='md'
            borderColor={mode("gray.300", "gray.600")}
            _focus={{
              borderColor: "blue.500",
              boxShadow: `0 0 0 1px ${mode("blue.500", "blue.400")}`,
            }}
          />
        </FormControl>

        <HStack spacing={4} align='flex-start'>
          <FormControl>
            <FormLabel fontWeight='600' fontSize='sm' mb={1}>
              Console (Optional)
            </FormLabel>
            <Select
              value={selectedConsoleId}
              onChange={(e) => setSelectedConsoleId(e.target.value)}
              placeholder='Select console'
              size='sm'
              borderRadius='md'
              borderColor={mode("gray.300", "gray.600")}
              _focus={{
                borderColor: "blue.500",
                boxShadow: `0 0 0 1px ${mode("blue.500", "blue.400")}`,
              }}
            >
              <option value=''>None</option>
              {gameConsoles.map((console) => (
                <option key={console.id} value={console.id}>
                  {console.name}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel fontWeight='600' fontSize='sm' mb={1}>
              Model (Optional)
            </FormLabel>
            <Select
              value={selectedModelId}
              onChange={(e) => setSelectedModelId(e.target.value)}
              placeholder='Select model'
              size='sm'
              borderRadius='md'
              borderColor={mode("gray.300", "gray.600")}
              _focus={{
                borderColor: "blue.500",
                boxShadow: `0 0 0 1px ${mode("blue.500", "blue.400")}`,
              }}
              isDisabled={!selectedConsoleId}
            >
              <option value='ALL'>All Models</option>
              {selectedConsoleId && getSelectedConsole()?.models && getSelectedConsole().models.length > 0
                ? getSelectedConsole().models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))
                : null}
            </Select>
          </FormControl>
        </HStack>

        <FormControl>
          <FormLabel fontWeight='600' fontSize='sm' mb={1}>
            Progress
          </FormLabel>
          <Select
            value={selectedProgress}
            onChange={(e) => setSelectedProgress(e.target.value)}
            size='sm'
            borderRadius='md'
            borderColor={mode("gray.300", "gray.600")}
            _focus={{
              borderColor: "blue.500",
              boxShadow: `0 0 0 1px ${mode("blue.500", "blue.400")}`,
            }}
          >
            <option value='onTakeJob'>On Take Job</option>
            <option value='onProgress'>On Progress</option>
            <option value='onCompletion'>On Completion</option>
          </Select>
        </FormControl>
      </VStack>

      <VStack spacing={3} align='stretch' mb={6}>
        <Heading size='sm' fontWeight='600'>
          Items
        </Heading>
        <VStack spacing={3} align='stretch'>
          {items.map((item, index) => (
            <Box
              key={item.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              p={4}
              borderWidth='1px'
              borderColor={mode("gray.200", "gray.700")}
              borderRadius='md'
              bg={draggedIndex === index ? mode("blue.50", "blue.900") : mode("gray.50", "gray.700")}
              opacity={draggedIndex === index ? 0.6 : 1}
              cursor={draggedIndex === index ? "grabbing" : "grab"}
              transition='all 0.2s'
            >
              <HStack mb={3} justify='space-between' spacing={1}>
                <HStack spacing={1} flex={1}>
                  <IconButton
                    icon={<DragHandleIcon />}
                    size='xs'
                    variant='ghost'
                    colorScheme='gray'
                    cursor='grab'
                    _active={{ cursor: "grabbing" }}
                    aria-label='Drag handle'
                    tabIndex={-1}
                  />
                  <FormLabel flex={1} mb={0} fontWeight='600' fontSize='sm'>
                    Item {index + 1}
                  </FormLabel>
                </HStack>
                <HStack spacing={1}>
                  <IconButton
                    icon={<ChevronUpIcon />}
                    size='xs'
                    variant='ghost'
                    colorScheme='blue'
                    onClick={() => moveItemUp(index)}
                    isDisabled={index === 0}
                    aria-label='Move up'
                  />
                  <IconButton
                    icon={<ChevronDownIcon />}
                    size='xs'
                    variant='ghost'
                    colorScheme='blue'
                    onClick={() => moveItemDown(index)}
                    isDisabled={index === items.length - 1}
                    aria-label='Move down'
                  />
                  <IconButton
                    icon={<DeleteIcon />}
                    size='xs'
                    variant='ghost'
                    colorScheme='red'
                    onClick={() => removeItem(item.id)}
                    aria-label='Remove item'
                    _hover={{ bg: mode("red.100", "red.900") }}
                  />
                </HStack>
              </HStack>

              <VStack spacing={3} align='stretch'>
                <FormControl>
                  <FormLabel fontSize='xs' fontWeight='600' mb={1}>
                    Type
                  </FormLabel>
                  <Select
                    value={item.type}
                    onChange={(e) => updateItem(item.id, "type", e.target.value)}
                    size='xs'
                    borderRadius='md'
                    borderColor={mode("gray.300", "gray.600")}
                  >
                    <option value={ITEM_TYPES.CHECKBOX}>Checkbox (Y/N)</option>
                    <option value={ITEM_TYPES.THREE_OPTION}>Yes/No/N.A.</option>
                    <option value={ITEM_TYPES.MULTIPLE_CHOICE}>Multiple Choice</option>
                    <option value={ITEM_TYPES.TEXT}>Text Field</option>
                    <option value={ITEM_TYPES.NUMERIC}>Numeric Field</option>
                    <option value={ITEM_TYPES.IMAGE}>Images (Upload)</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize='xs' fontWeight='600' mb={1}>
                    Question
                  </FormLabel>
                  <Input
                    value={item.question}
                    onChange={(e) => updateItem(item.id, "question", e.target.value)}
                    placeholder='Enter question'
                    size='xs'
                    borderRadius='md'
                    borderColor={mode("gray.300", "gray.600")}
                  />
                </FormControl>

                {item.type === ITEM_TYPES.MULTIPLE_CHOICE && (
                  <FormControl>
                    <FormLabel fontSize='xs' fontWeight='600' mb={2}>
                      Options
                    </FormLabel>
                    <VStack spacing={2} align='stretch'>
                      {item.options.map((option, optIndex) => (
                        <HStack key={optIndex} spacing={2}>
                          <Input
                            size='xs'
                            value={option}
                            onChange={(e) => updateOption(item.id, optIndex, e.target.value)}
                            placeholder={`Option ${optIndex + 1}`}
                            borderRadius='md'
                            borderColor={mode("gray.300", "gray.600")}
                          />
                          <IconButton
                            icon={<DeleteIcon />}
                            size='xs'
                            variant='ghost'
                            colorScheme='red'
                            onClick={() => removeOption(item.id, optIndex)}
                            aria-label='Remove option'
                            _hover={{ bg: mode("red.100", "red.900") }}
                          />
                        </HStack>
                      ))}
                      <Button
                        size='xs'
                        leftIcon={<AddIcon />}
                        onClick={() => addOption(item.id)}
                        variant='outline'
                        colorScheme='blue'
                      >
                        Add Option
                      </Button>
                    </VStack>
                  </FormControl>
                )}
              </VStack>
            </Box>
          ))}
        </VStack>

        <Button leftIcon={<AddIcon />} onClick={addItem} colorScheme='blue' variant='outline' w='full' size='sm'>
          Add Item
        </Button>
      </VStack>

      <Divider my={6} />

      <HStack justify='flex-end' spacing={2}>
        <Button variant='outline' onClick={() => onSaveComplete && onSaveComplete()} size='sm'>
          Cancel
        </Button>
        {editingId && (
          <Button variant='outline' onClick={handleClearForm} size='sm'>
            Clear
          </Button>
        )}
        <Button colorScheme='blue' onClick={handleSave} size='sm'>
          {editingId ? "Update" : "Save"}
        </Button>
      </HStack>
    </Box>
  );
};

export default SetupCheckList;
